type StorageKind = 'localStorage' | 'sessionStorage';

function getStorage(kind: StorageKind): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window[kind] ?? null;
  } catch {
    return null;
  }
}

function tryGet(kind: StorageKind, key: string): string | null {
  const storage = getStorage(kind);
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function trySet(kind: StorageKind, key: string, value: string): boolean {
  const storage = getStorage(kind);
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function tryRemove(kind: StorageKind, key: string): boolean {
  const storage = getStorage(kind);
  if (!storage) {
    return false;
  }

  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function tryClear(kind: StorageKind): boolean {
  const storage = getStorage(kind);
  if (!storage) {
    return false;
  }

  try {
    storage.clear();
    return true;
  } catch {
    return false;
  }
}

export interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): boolean;
  removeItem(key: string): boolean;
  clear(): boolean;
}

export const safeLocalStorage: SafeStorage = {
  getItem: (key) => tryGet('localStorage', key),
  setItem: (key, value) => trySet('localStorage', key, value),
  removeItem: (key) => tryRemove('localStorage', key),
  clear: () => tryClear('localStorage'),
};

export const safeSessionStorage: SafeStorage = {
  getItem: (key) => tryGet('sessionStorage', key),
  setItem: (key, value) => trySet('sessionStorage', key, value),
  removeItem: (key) => tryRemove('sessionStorage', key),
  clear: () => tryClear('sessionStorage'),
};
